// src/lib/automation/triggerEngine.js
// ─────────────────────────────────────────────────────────────────────────────
//  Central, pure-JS trigger engine for Sheepheard.
//
//  PATTERN:
//    1. Feature code calls  emit(EVENT_TYPE, payload)
//    2. Engine runs all registered handlers for that event type
//    3. Each handler receives (payload, stores) and can read/write any store
//
//  This is a synchronous, in-memory pub/sub — no network, no async.
//  Handlers are deterministic: same input → same output.
// ─────────────────────────────────────────────────────────────────────────────

const _handlers = {};    // { eventType: [handler, handler, ...] }
const _stores   = {};    // { storeKey: { get, set } } — bound by useTriggerEngine

// ── Register a handler for an event type ──────────────────────────────────
export function on(eventType, handler) {
  if (!_handlers[eventType]) _handlers[eventType] = [];
  _handlers[eventType].push(handler);
  // Return unsubscribe function
  return () => {
    _handlers[eventType] = _handlers[eventType].filter(h => h !== handler);
  };
}

// ── Bind a store so handlers can read/write it ────────────────────────────
export function bindStore(key, getter, setter) {
  _stores[key] = { get: getter, set: setter };
}

// ── Unbind a store (cleanup on unmount) ───────────────────────────────────
export function unbindStore(key) {
  delete _stores[key];
}

// ── Emit an event — runs all handlers synchronously ───────────────────────
export function emit(eventType, payload = {}) {
  const handlers = _handlers[eventType];
  if (!handlers || handlers.length === 0) return;

  for (const handler of handlers) {
    try {
      handler(payload, _stores);
    } catch (err) {
      console.error(`[TriggerEngine] Handler error for ${eventType}:`, err);
    }
  }
}

// ── Convenience: get a store's current value ──────────────────────────────
export function getStore(key) {
  return _stores[key]?.get?.() ?? null;
}

// ── Convenience: set a store's value ──────────────────────────────────────
export function setStore(key, valueOrUpdater) {
  const store = _stores[key];
  if (!store) {
    console.warn(`[TriggerEngine] Store "${key}" not bound`);
    return;
  }
  store.set(valueOrUpdater);
}
